import {
  BillingPreferenceResponse,
  BillingPreferenceUpdateSchema,
  type ClientByIdResponse,
  type ClientCreateRequest,
  type ClientCsvResponse,
  type ClientGetAllParams,
  type ClientGetAllResponse,
  type ClientResponse,
  type ClientUpdateRequest,
  type ClientUpdateStatusRequest,
  type ClientUpdateStatusResponse,
  type ContactGetAllParams,
  type ContactGetAllResponse,
  type ContactStatusUpdateRequest,
} from '@/types/client.types';
import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import axios, { AxiosError, AxiosResponse } from 'axios';
import axiosInstance from './axiosInstance';
import { normalizeAxiosError } from '@/lib/error';
import { request } from '@/services/request';
import type { GenericErrorResponse } from '@/types/api.types';

type ClientApiResult<T> = { data?: T; error?: GenericErrorResponse };

export const getClientById = async (clientId: string): Promise<ClientApiResult<ClientByIdResponse>> => {
  try {
    const response = await axiosInstance.get<ClientByIdResponse>(`/clients/${clientId}`);
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const useClientById = (
  clientId: string | undefined,
  options?: { refreshKey?: number; enabled?: boolean }
) =>
  useQuery<ClientByIdResponse>({
    queryKey: ['client', clientId, options?.refreshKey],
    queryFn: async () => {
      const { data, error } = await getClientById(clientId!);
      if (error) throw error;
      return data!;
    },
    enabled: (options?.enabled ?? true) && !!clientId,
    staleTime: 1000 * 60 * 5,
  });

export const getContactsCsv = async (
  clientId: string
): Promise<{
  data?: string;
  error?: GenericErrorResponse;
}> => {
  const path = `clients/${clientId}/contacts/csv`;
  try {
    const response = await axios.post<string>(
      path,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const useClientBillingPreferenceGet = (clientId: string | undefined, options?: { enabled?: boolean }) => {
  return useQuery<AxiosResponse<BillingPreferenceResponse>, GenericErrorResponse>({
    queryKey: ['client', 'billing-preferences', clientId],
    queryFn: async () => {
      if (!clientId) throw new Error('Client ID is required');
      const response = await axios.get<BillingPreferenceResponse>(`/clients/${clientId}/billing-preferences`);
      return response;
    },
    enabled: (options?.enabled ?? true) && !!clientId,
  });
};

export const useClientBillingPreferenceUpdate = () => {
  return useMutation<
    BillingPreferenceResponse,
    AxiosError<GenericErrorResponse>,
    { clientId: string; data: BillingPreferenceUpdateSchema }
  >({
    mutationFn: async ({ clientId, data }) => {
      const response = await axios.put<BillingPreferenceResponse>(`/clients/${clientId}/billing-preferences`, {
        ...data,
      });
      return response.data;
    },
  });
};

type QueryOptions<T> = Partial<UseQueryOptions<T, GenericErrorResponse>>;

export const fetchClients = (params?: ClientGetAllParams) =>
  request(() => axiosInstance.get<ClientGetAllResponse>('/clients/', { params }));

/** Full client record, including contacts. `getClientById` above returns the narrower ClientByIdResponse. */
export const fetchClient = (clientId: string) =>
  request(() => axiosInstance.get<ClientResponse>(`/clients/${encodeURIComponent(clientId)}`));

export const fetchContacts = (params?: ContactGetAllParams) =>
  request(() => axiosInstance.get<ContactGetAllResponse>('/contacts/', { params }));

/** `options.query.queryKey`, when given, replaces the params in the cache key (DataTable passes its own). */
export const useClients = (params?: ClientGetAllParams, options?: { query?: QueryOptions<ClientGetAllResponse> }) => {
  const { queryKey, ...queryOptions } = options?.query ?? {};
  return useQuery<ClientGetAllResponse, GenericErrorResponse>({
    ...queryOptions,
    queryKey: ['clients', ...(queryKey ?? [params])],
    queryFn: () => fetchClients(params),
  });
};

export const useContacts = (
  params?: ContactGetAllParams,
  options?: { query?: QueryOptions<ContactGetAllResponse> }
) => {
  const { queryKey, ...queryOptions } = options?.query ?? {};
  return useQuery<ContactGetAllResponse, GenericErrorResponse>({
    ...queryOptions,
    queryKey: ['contacts', ...(queryKey ?? [params])],
    queryFn: () => fetchContacts(params),
  });
};

export const useClient = (clientId: string | undefined) =>
  useQuery<ClientResponse, GenericErrorResponse>({
    queryKey: ['client', 'full', clientId],
    queryFn: () => fetchClient(clientId!),
    enabled: !!clientId,
  });

export const useCreateClient = () =>
  useMutation<ClientResponse, GenericErrorResponse, { data: ClientCreateRequest }>({
    mutationFn: ({ data }) => request(() => axiosInstance.post<ClientResponse>('/clients/', data)),
  });

export const useUpdateClient = () =>
  useMutation<ClientResponse, GenericErrorResponse, { id: string; data: ClientUpdateRequest }>({
    mutationFn: ({ id, data }) =>
      request(() => axiosInstance.put<ClientResponse>(`/clients/${encodeURIComponent(id)}`, data)),
  });

export const useUpdateClientStatus = () =>
  useMutation<ClientUpdateStatusResponse, GenericErrorResponse, { id: string; data: ClientUpdateStatusRequest }>({
    mutationFn: ({ id, data }) =>
      request(() =>
        axiosInstance.put<ClientUpdateStatusResponse>(`/clients/${encodeURIComponent(id)}/status`, data)
      ),
  });

export const useExportClientsCsv = () =>
  useMutation<ClientCsvResponse, GenericErrorResponse, void>({
    // The server builds the whole list; the SDK this replaced had no timeout, so don't apply the 15s default.
    mutationFn: () =>
      request(() => axiosInstance.post<ClientCsvResponse>('/clients/csv', undefined, { timeout: 5 * 60 * 1000 })),
  });

export const useUpdateContactStatus = () =>
  useMutation<{ message?: string }, GenericErrorResponse, { id: string; data: ContactStatusUpdateRequest }>({
    mutationFn: ({ id, data }) =>
      request(() =>
        axiosInstance.put<{ message?: string }>(`/contacts/${encodeURIComponent(id)}/status`, data)
      ),
  });
